import React from "react";
import { Star, Quotes } from "@phosphor-icons/react";

/**
 * Public testimonials strip for a client's career page.
 * Renders a horizontal-scrolling grid of cards on mobile and a
 * responsive grid on desktop.
 */
export default function CareerTestimonials({ items, avgRating }) {
    if (!items || items.length === 0) return null;

    return (
        <section
            data-testid="career-testimonials-section"
            className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-14 border-t border-[#E5E5E5]"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt mb-2">
                        Placed candidates say
                    </p>
                    <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Stories from people we&apos;ve hired.
                    </h2>
                </div>
                {avgRating && (
                    <div
                        data-testid="career-avg-rating"
                        className="flex items-center gap-2 bg-ink text-white px-4 py-2"
                    >
                        <Star size={16} weight="fill" className="text-splash" />
                        <span className="font-heading text-2xl font-black tracking-tighter">
                            {avgRating}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/70">
                            / 5 · {items.length} review{items.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((t) => (
                    <article
                        key={t.id}
                        data-testid={`career-testimonial-${t.id}`}
                        className="bg-white border border-[#E5E5E5] p-5 sm:p-6 relative"
                    >
                        <Quotes
                            size={32}
                            weight="fill"
                            className="text-splash/40 absolute top-4 right-4"
                        />
                        <div className="flex gap-0.5 mb-3">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                    key={n}
                                    size={14}
                                    weight="fill"
                                    className={n <= (t.rating || 0) ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                />
                            ))}
                        </div>
                        <p className="text-sm text-[#262626] leading-relaxed mb-4">
                            &ldquo;{t.text}&rdquo;
                        </p>
                        <div className="flex items-center justify-between pt-4 border-t border-[#F5F5F5]">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-ink truncate">
                                    {t.candidate_name || "Placed candidate"}
                                </p>
                                {t.candidate_title && (
                                    <p className="text-[11px] text-[#525252] truncate">
                                        {t.candidate_title}
                                        {t.client_name ? ` · ${t.client_name}` : ""}
                                    </p>
                                )}
                            </div>
                            {t.created_at && (
                                <p className="text-[10px] text-[#A3A3A3] shrink-0 ml-2">
                                    {new Date(t.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                                </p>
                            )}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
